<template>
  <BaseRender
    class="salary-field-mapping"
    showDefaultValue
    :loading="loading"
    :fieldTypeInfo="fieldTypeInfo"
    :fieldInfo="fieldInfo"
    :mappingList="mappingList"
    :jobCustomField="jobCustomField"
    @save="handleSave"
    @jobCustomFieldChange="handleJobCustomFieldChange"
  >
    <template #mappingTypeSwitchContainer>
      <div class="mapping-type-switch-container">
        <label class="mapping-type-label">{{ $t(`${i18nPrefix}.label.mappingType`) }}</label>
        <el-switch
          v-model="isCustomMapping"
          class="mapping-type-value"
          active-color="#0096cc"
          inactive-color="#13ce66"
          :active-text="$t(`${i18nPrefix}.label.customMapping`)"
          :inactive-text="$t(`${i18nPrefix}.label.jobCustomField`)"
          >
        </el-switch>
      </div>
    </template>
    <template v-if="!isCustomMapping" #jobCustomFieldMappingType>
      <Salary
        ref="JobCustomFieldMappingType"
        :jobCustomFieldMappingVal="jobCustomFieldMappingVal"
        :salaryMonthList="salaryMonthList"
        :jobCustomFieldList="jobCustomFieldList"
      >
      </Salary>
    </template>
    <template #defaultValue="{ refName }">
      <el-cascader
        v-model="defaultSalaryRange"
        class="salary-range"
        clearable
        filterable
        separator=" -- "
        :ref="`${refName}_range`"
        :options="salaryRangeList"
        :props="{ value: 'id', label: 'i18nName' }"
        :placeholder="$t(`${i18nPrefix}.placeholder.defaultValue`)"
        @focus="clearError(`${refName}_range`)"
      ></el-cascader>
      <el-select
        v-model="defaultSalaryMonth"
        class="salary-month"
        clearable
        :ref="`${refName}_month`"
        :placeholder="$t(`${i18nPrefix}.placeholder.defaultValue`)"
        @focus="clearError(`${refName}_month`)"
      >
        <el-option
          v-for="(item, index) in salaryMonthList"
          :key="index"
          :value="item.id"
          :label="item.i18nName"
        ></el-option>
      </el-select>
    </template>
    <template #selfFieldRender="{ data }">
      <span class="self-field-name">{{ data.i18nName }}</span>
    </template>
    <template #otherFieldRender="{ data, refName }">
      <el-cascader
        v-model="data.range"
        class="salary-range"
        clearable
        filterable
        separator=" -- "
        :ref="`${refName}_range`"
        :options="salaryRangeList"
        :props="{ value: 'id', label: 'i18nName' }"
        @focus="clearError(`${refName}_range`)"
      ></el-cascader>
      <el-select
        v-model="data.month"
        class="salary-month"
        clearable
        :ref="`${refName}_month`"
        @focus="clearError(`${refName}_month`)"
      >
        <el-option
          v-for="(item, index) in salaryMonthList"
          :key="index"
          :value="item.id"
          :label="item.i18nName"
        ></el-option>
      </el-select>
    </template>
  </BaseRender>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import channelId from '../channelId'
import mixin from '../mixin'
import Salary from '../common/salary.vue'
import { addI18nProperty } from '@/i18n'

const SALARY_PAYMENT_TYPE_OPTION = {
  DEFAULT_VALUE: 1
}

const SALARY_TYPE_OPTION = {
  FIXED_VALUE: 1,
  CUSTOM_FIELDS: 2
}

const SALARY_MONTHS_OPTION = {
  DEFAULT_VALUE: 1
}

class OtherSalary {
  constructor (minSalary, maxSalary, month, key = 'value') {
    this.minSalary = minSalary
    this.maxSalary = maxSalary
    this.salaryMonth = month
    this.key = key
  }

  get range () {
    const min = this.minSalary[this.key]
    const max = this.maxSalary[this.key]
    return [min, max]
  }

  set range (val) {
    this.minSalary[this.key] = val[0]
    this.maxSalary[this.key] = val[1]
  }

  get month () {
    return this.salaryMonth[this.key]
  }

  set month (val) {
    this.salaryMonth[this.key] = val
  }
}

function createEmptyOtherSalary () {
  return new OtherSalary({ value: null }, { value: null }, { value: null })
}

export default {
  name: `channel-${channelId.liepin}-fieldType-salary`,
  mixins: [mixin],
  components: {
    Salary
  },
  data () {
    return {
      mappingList: [],
      jobSalaryList: [],
      salaryMonthList: [],
      salaryRangeList: [],
      jobCustomField: {
        show: true,
        fieldId: null,
        field: null,
        fieldList: []
      },
      jobCustomFieldList: [],
      isCustomMapping: false,
      jobCustomFieldMappingVal: {
        salaryType: {
          type: SALARY_PAYMENT_TYPE_OPTION.DEFAULT_VALUE,
          value: null
        },
        minSalary: {
          type: SALARY_TYPE_OPTION.FIXED_VALUE,
          value: null,
          customFiledValue: null
        },
        maxSalary: {
          type: SALARY_TYPE_OPTION.FIXED_VALUE,
          value: null,
          customFiledValue: null
        },
        salaryMonth: {
          type: SALARY_MONTHS_OPTION.DEFAULT_VALUE,
          value: null
        }
      }
    }
  },
  computed: {
    minSalaryField () {
      return this.fieldInfo.salary_min
    },
    maxSalaryField () {
      return this.fieldInfo.salary_max
    },
    salaryMonthField () {
      return this.fieldInfo.salary_month
    },
    defaultSalaryRange: {
      get () {
        if (this.minSalaryField.default) {
          return [this.minSalaryField.default, this.maxSalaryField.default]
        }
        return []
      },
      set (val) {
        this.minSalaryField.default = val[0]
        this.maxSalaryField.default = val[1]
      }
    },
    defaultSalaryMonth: {
      get () {
        return this.salaryMonthField.default
      },
      set (val) {
        this.salaryMonthField.default = val
      }
    }
  },
  created () {
    this.refreshPageData()
  },
  methods: {
    ...mapActions('job', ['getJobSalaryList']),
    ...mapActions('jobPosting', ['getPostingFieldOptions']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    getDataFromApi () {
      return Promise.all([
        this.getJobSalaryList().then(
          resp => {
            if (resp) {
              _.forEach(resp, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
              this.jobSalaryList = resp
            }
          }
        ),
        this.getPostingFieldOptions({ fieldTypeId: this.fieldTypeInfo.id, params: { need_children: true } }).then(
          resp => {
            if (resp) {
              const salaryMonthList = []
              const salaryRangeList = []
              _.forEach(resp, option => {
                addI18nProperty(option, 'i18nName', 'cn_name', 'name')
                if (option.children) {
                  _.forEach(option.children, child => addI18nProperty(child, 'i18nName', 'cn_name', 'name'))
                }
                if (option.value.includes('月')) {
                  salaryMonthList.push(option)
                } else {
                  salaryRangeList.push(option)
                }
              })
              this.salaryMonthList = salaryMonthList
              this.salaryRangeList = salaryRangeList
            }
          }
        ),
        this.searchJobCustomField({ status: 1, type: 'SELECT,SINGLE,TEXT,TEXTAREA', need_options: true }).then(
          resp => {
            if (resp) {
              const fieldList = []
              _.forEach(resp, field => {
                if (field.type === 'SELECT') {
                  fieldList.push(field)
                }
                addI18nProperty(field, 'i18nName', 'cn_name', 'name')
              })
              this.jobCustomFieldList = resp
              this.jobCustomField.fieldList = fieldList
              const fieldId = this.jobCustomField.fieldId
              if (fieldId) {
                this.jobCustomField.field = _.find(fieldList, { id: fieldId })
              }
            }
          }
        )
      ])
    },
    setOtherField (name, dataMap, otherFieldMap) {
      dataMap.key = parseInt(dataMap.key) || null
      dataMap.value = parseInt(dataMap.value) || null
      const dataKey = dataMap.key
      let otherField = null
      if (otherFieldMap.hasOwnProperty(dataKey)) {
        otherField = otherFieldMap[dataKey]
      } else {
        otherFieldMap[dataKey] = otherField = new OtherSalary()
      }
      otherField[name] = dataMap
    },
    async refreshPageData (noNeedApiData) {
      this.loading = true
      try {
        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        if (minSalaryField.default) {
          minSalaryField.default = parseInt(minSalaryField.default)
          maxSalaryField.default = parseInt(maxSalaryField.default)
          salaryMonthField.default = parseInt(salaryMonthField.default)
        }
        let isJobCustomField = false
        if (minSalaryField.data_source === 'CUSTOM_FIELD') {
          if (minSalaryField.data_key_path) {
            const fieldId = parseInt(minSalaryField.data_key_path)
            this.jobCustomField.fieldId = fieldId
            if (!this.jobCustomField.field) {
              this.jobCustomField.field = _.find(this.jobCustomField.fieldList, { id: fieldId })
            }
          }
          isJobCustomField = true
        }
        if (!noNeedApiData) {
          await this.getDataFromApi()
        }
        if (!minSalaryField.data_source || minSalaryField.data_source === 'CUSTOM_DEFAULT') {
          this.isCustomMapping = false
          this.jobCustomFieldMappingVal = {
            minSalary: {
              type: minSalaryField.data_key_path === 'FIXED' ? SALARY_TYPE_OPTION.FIXED_VALUE : SALARY_TYPE_OPTION.CUSTOM_FIELDS,
              value: minSalaryField.data_key_path === 'FIXED' ? minSalaryField.default : null,
              customFiledValue: minSalaryField.data_key_path === 'FIXED' ? null : parseInt(minSalaryField.default) || null
            },
            maxSalary: {
              type: maxSalaryField.data_key_path === 'FIXED' ? SALARY_TYPE_OPTION.FIXED_VALUE : SALARY_TYPE_OPTION.CUSTOM_FIELDS,
              value: maxSalaryField.data_key_path === 'FIXED' ? maxSalaryField.default : null,
              customFiledValue: maxSalaryField.data_key_path === 'FIXED' ? null : parseInt(maxSalaryField.default) || null
            },
            salaryMonth: {
              type: SALARY_MONTHS_OPTION.DEFAULT_VALUE,
              value: salaryMonthField.default
            }
          }
          minSalaryField.default = null
          maxSalaryField.default = null
          salaryMonthField.default = null
          minSalaryField.data_map_list = []
          maxSalaryField.data_map_list = []
          salaryMonthField.data_map_list = []
        } else {
          this.isCustomMapping = true
        }
        const otherFieldMap = {}
        const minSalaryDataMapList = minSalaryField.data_map_list
        const maxSalaryDataMapList = maxSalaryField.data_map_list
        const salaryMonthDataMapList = salaryMonthField.data_map_list
        _.forEach(minSalaryDataMapList, (minSalaryDataMap, index) => {
          const maxSalaryDataMap = maxSalaryDataMapList[index]
          const salaryMonthDataMap = salaryMonthDataMapList[index]

          this.setOtherField('minSalary', minSalaryDataMap, otherFieldMap)
          this.setOtherField('maxSalary', maxSalaryDataMap, otherFieldMap)
          this.setOtherField('salaryMonth', salaryMonthDataMap, otherFieldMap)
        })

        const selfFieldList = isJobCustomField ? this.jobCustomField.field.options : this.jobSalaryList
        this.mappingList = _.map(selfFieldList, (item, index) => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          let otherField = null
          if (otherFieldMap.hasOwnProperty(item.id)) {
            otherField = otherFieldMap[item.id]
          } else {
            otherField = createEmptyOtherSalary()
          }
          return {
            id: index + 1,
            self: item,
            other: otherField
          }
        })
      } finally {
        this.loading = false
      }
    },
    handleJobCustomFieldChange (fieldId) {
      const selfFieldList = fieldId ? this.jobCustomField.field.options : this.jobSalaryList
      this.mappingList = _.map(selfFieldList, (item, index) => {
        addI18nProperty(item, 'i18nName', 'cn_name', 'name')
        return {
          id: index + 1,
          self: item,
          other: createEmptyOtherSalary()
        }
      })
    },
    handleSave () {
      if (this.isCustomMapping) {
        const errorNames = []
        if (!this.defaultSalaryRange || this.defaultSalaryRange.length < 1) {
          errorNames.push('defaultValue_range')
        }
        if (!this.defaultSalaryMonth) {
          errorNames.push('defaultValue_month')
        }
        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        const isJobCustomField = Boolean(this.jobCustomField.fieldId)
        const dataMapList = []
        let hasOldData = false
        _.forEach(this.mappingList, item => {
          const otherField = item.other
          const selfField = item.self
          if (!otherField.minSalary.value && !otherField.maxSalary.value && !otherField.salaryMonth.value) {
            return
          }
          if (!otherField.minSalary.value || !otherField.maxSalary.value) {
            errorNames.push(`other_${item.id}_range`)
          }
          if (!otherField.salaryMonth.value) {
            errorNames.push(`other_${item.id}_month`)
          }
          if (otherField.minSalary.id) {
            hasOldData = true
            return
          }
          dataMapList.push({
            key: selfField.id,
            minSalary: otherField.minSalary.value,
            maxSalary: otherField.maxSalary.value,
            salaryMonth: otherField.salaryMonth.value
          })
        })
        if (errorNames.length > 0) {
          this.setError(errorNames)
          return
        }
        if (!hasOldData) {
          minSalaryField.data_map_list = []
          maxSalaryField.data_map_list = []
          salaryMonthField.data_map_list = []
        }
        if (isJobCustomField) {
          minSalaryField.data_source = 'CUSTOM_FIELD'
          minSalaryField.data_key_path = `${this.jobCustomField.fieldId}`
          maxSalaryField.data_source = 'CUSTOM_FIELD'
          maxSalaryField.data_key_path = `${this.jobCustomField.fieldId}`
          salaryMonthField.data_source = 'CUSTOM_FIELD'
          salaryMonthField.data_key_path = `${this.jobCustomField.fieldId}`
        } else {
          minSalaryField.data_source = 'SALARY'
          minSalaryField.data_key_path = 'id'
          maxSalaryField.data_source = 'SALARY'
          maxSalaryField.data_key_path = 'id'
          salaryMonthField.data_source = 'SALARY'
          salaryMonthField.data_key_path = 'id'
        }
        _.forEach(dataMapList, item => {
          minSalaryField.data_map_list.push({
            key: item.key,
            value: item.minSalary
          })
          maxSalaryField.data_map_list.push({
            key: item.key,
            value: item.maxSalary
          })
          salaryMonthField.data_map_list.push({
            key: item.key,
            value: item.salaryMonth
          })
        })
      } else {
        const isValid = this.$refs.JobCustomFieldMappingType.validate()
        if (!isValid) {
          return
        }
        const mapping = this.jobCustomFieldMappingVal
        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        minSalaryField.data_source = 'CUSTOM_DEFAULT'
        minSalaryField.data_key_path = mapping.minSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? 'FIXED' : 'JOB_CUSTOM_FIELD'
        minSalaryField.default = mapping.minSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? mapping.minSalary.value : mapping.minSalary.customFiledValue
        minSalaryField.data_map_list = []

        maxSalaryField.data_source = 'CUSTOM_DEFAULT'
        maxSalaryField.data_key_path = mapping.maxSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? 'FIXED' : 'JOB_CUSTOM_FIELD'
        maxSalaryField.default = mapping.maxSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? mapping.maxSalary.value : mapping.maxSalary.customFiledValue
        maxSalaryField.data_map_list = []

        salaryMonthField.data_source = 'CUSTOM_DEFAULT'
        salaryMonthField.data_key_path = 'ORIGIN'
        salaryMonthField.default = mapping.salaryMonth.type === SALARY_TYPE_OPTION.FIXED_VALUE ? mapping.salaryMonth.value : mapping.salaryMonth.customFiledValue
        salaryMonthField.data_map_list = []
      }
      this.$emit('save')
    }
  }
}
</script>

<style lang="scss" scoped>
.salary-field-mapping {
  ::v-deep {
    .default-value {
      display: flex;
    }

    .salary-range, .salary-month {
      width: 100%;
    }

    .salary-range {
      margin-right: 10px;
    }

    .salary-month {
      margin-left: 10px;
    }

    .el-table {
      .el-table__body {
        .self-field {
          .el-select {
            .el-input__inner {
              padding-left: 5px;
            }
          }
        }

        .other-field {
          .cell {
            display: flex;
            padding-left: 5px;
            padding-right: 5px;
          }
        }
      }
    }
  }
}
</style>
